<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-flex-grid :rows="[['132px', '216px', '200px', '200px', '300px', '200px']]" :margin-bottom="'30px'">
        <template #col-1-1><app-button @click.native="onShowInvoiceModal(null)">Create invoice</app-button></template>
        <template #col-1-2>
          <app-filters
            @change="onChangeFilter"
            v-model="filter_paid"
            :filters="filter_paid_options"
            name="filterPaid"
          />
        </template>
        <template #col-1-3>
          <app-select
            @input="onChangeFilter"
            v-model="filter_legal_entity"
            :options="legal_entity_options"
            :reduce="item => item.code"
            label="short_name"
            placeholder="Legal entity"
          />
        </template>
        <template #col-1-4>
          <app-select
            @input="onChangeFilter"
            v-model="filter_payment_method"
            :options="payment_method_options"
            :reduce="item => item.code"
            :searchable="false"
            placeholder="Payment method"
          />
        </template>
        <template #col-1-5>
          <app-select
            @input="onChangeFilter"
            @search="onSupplierSearch"
            v-model="filter_supplier"
            :options="supplier_options"
            :display-field="option => `${option.trade_name === option.legal_name ? option.trade_name : `${option.trade_name} (${option.legal_name})`}`"
            label="trade_name"
            placeholder="Supplier"
          />
        </template>
        <template #col-1-6>
          <app-select
            @input="onChangeFilter"
            v-model="filter_source"
            :options="pos_transaction_source_options"
            :reduce="item => item.code"
            placeholder="Source"
          />
        </template>
      </app-flex-grid>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__supplier><div class="table-default__left">Supplier</div></template>
        <template #h__responsible><div class="table-default__left">Responsible</div></template>
        <template #date="{ row }">{{ dateFormat(row.date) }}</template>
        <template #supplier="{ row }">
          <div class="table-default__left">
            <router-link
              :to="{ name: 'administrative-suppliers-detail-purchases', params: { id: row.supplier.id } }"
              class="table-default__link-inline"
            >
              {{ row.supplier.legal_name }}
            </router-link>
          </div>
        </template>
        <template #responsible="{ row }"><div class="table-default__left">{{ row.responsible?.full_name || '-' }}</div></template>
        <template #amount="{ row }">
          {{ amountFormat(row.amount_total) }} {{ row.currency.code }}
        </template>
        <template #invoice_number="{ row }">{{ row.invoice_number || '-' }}</template>
        <template #payment_method="{ row }">{{ row.supplier_payment_method.payment_method.name || '-' }}</template>
        <template #account="{ row }">
          <template v-if="row.supplier_payment_method.payment_method.code === 'bank'">
            <template v-if="row.supplier_payment_method.bank_account">
              {{ row.supplier_payment_method.bank_account.account_number }}<br>{{ row.supplier_payment_method.bank_account.bank.short_name }}
            </template>
            <template v-else>-</template>
          </template>
          <template v-else-if="row.supplier_payment_method.payment_method.code === 'mobile'">
            <template v-if="row.supplier_payment_method.mobile_account">
              {{ row.supplier_payment_method.mobile_account.account_number }}<br>{{ row.supplier_payment_method.mobile_account.provider.name }}
            </template>
            <template v-else>-</template>
          </template>
          <template v-else-if="row.supplier_payment_method.payment_method.code === 'gepg'">
            <template v-if="row.gepg_number">{{ row.gepg_number }}</template>
            <template v-else>-</template>
          </template>
          <template v-else>-</template>
        </template>
        <template #payment_plans="{ row }">
          <span
            :style="{ 'backgroundColor': row.payment_status === 'unpaid' ? '#e85c20' : row.payment_status === 'paid' ? '#77b017' : '#439ad1' }"
            class="status-tag"
          >
            {{ row.payment_status === 'unpaid' ? 'Unpaid' : row.payment_status === 'paid' ? 'Paid' : amountFormat(row.paid_amount) }}
          </span>
        </template>
        <template #attachment="{ row }">
          <div class="table-default__controls">
            <a
              :href="row.attachment?.file"
              target="_blank"
              :class="{'table-default__disabled': !row.attachment}"
            >
              <img
                src="@/assets/img/snacks-link.svg"
                alt="Attachment"
              >
            </a>
          </div>
        </template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onShowInvoiceModal(row)" type="button">
              <img src="@/assets/img/edit-table-icon.svg" alt="Edit">
            </button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
    </template>
    <modal
      name="invoice-modal"
      :width="800"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      class="modal-not-overflow"
    >
      <div class="popup">
        <button @click="$modal.hide('invoice-modal')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <template v-if="invoice_to_action">Invoice from {{ form.supplier.trade_name }}</template>
          <template v-else>
            Add invoice
            <app-checkbox
              v-model="form.archive"
              label="Archive"
            />
          </template>
        </div>
        <div class="popup__body">
          <form @submit.prevent="onCheckInvoiceForm" class="arrange-popup" :key="repaint_key">
            <app-grid>
              <template #item-1>
                <app-form-group label="Legal entity" required>
                  <app-select
                    v-model="form.legal_entity"
                    :options="legal_entity_options"
                    :reduce="item => item.code"
                    label="short_name"
                    :clearable="false"
                    placeholder="Legal entity"
                    :error="$v.form.legal_entity.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.legal_entity.$dirty && !$v.form.legal_entity.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Date" required>
                  <template v-if="!invoice_to_action" #additional>
                    <app-checkbox v-model="show_accounting_date" label="Set Accounting date" />
                  </template>
                  <app-date-picker
                    v-model="form.date"
                    :clearable="false"
                    :disabled-date="disableFutureDates"
                    placeholder="Specify a date"
                    :error="$v.form.date.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.date.$dirty && !$v.form.date.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group v-if="show_accounting_date || invoice_to_action" label="Accounting date" required>
                  <app-date-picker
                    v-model="form.accounting"
                    :clearable="false"
                    :disabled-date="disableFutureDates"
                    type="month"
                    format="MMM YYYY"
                    placeholder="Specify a date"
                    :error="$v.form.accounting.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.accounting.$dirty && !$v.form.accounting.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Invoice number" label-for="invoice-number" required>
                  <app-input
                    v-model="form.invoice_number"
                    id="invoice-number"
                    name="invoice-number"
                    placeholder="Invoice number"
                    :error="$v.form.invoice_number.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.invoice_number.$dirty && !$v.form.invoice_number.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Currency" required>
                  <app-cells :indent="false">
                    <app-radio
                      v-for="radio of currency_options"
                      :key="radio.label"
                      v-model="form.currency"
                      name="filterCurrency"
                      :value="radio.code"
                      :checked="form.currency === radio.code"
                    >
                      <template #label>{{ radio.label }}</template>
                    </app-radio>
                  </app-cells>
                </app-form-group>
                <app-form-group label="Responsible" required>
                  <app-select
                    @search="onEmployeeSearch"
                    v-model="form.responsible"
                    :options="responsible_options"
                    :clearable="false"
                    label="full_name"
                    placeholder="Responsible"
                    :error="$v.form.responsible.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.responsible.$dirty && !$v.form.responsible.required">Required field</div>
                  </template>
                </app-form-group>
                <app-form-group label="Attachment">
                  <template #additional>
                    <app-checkbox
                      v-if="!invoice_to_action && (efd_details && form.attachment)"
                      @change.native="onChangeReceiptSame"
                      v-model="receipt_same"
                      label="Receipt copy same"
                    />
                  </template>
                  <app-uploader
                    @onAddedFile="onChangeAttachment"
                    v-model="form.attachment"
                    :extensions="['application/pdf', 'image/jpeg', 'image/png']"
                    formDataFormats="PDF,JPEG,PNG"
                    class="custom-uploader--flex"
                  />
                </app-form-group>
                <app-form-group v-if="!invoice_to_action" class="form-group--mh">
                  <app-checkbox
                    @change.native="onChangeEFDDetails"
                    label="EFD Provided"
                    v-model="efd_details"
                    :readonly="!form.supplier || !form.supplier.verified"
                  />
                </app-form-group>
              </template>
              <template #item-2>
                <app-form-group v-if="!invoice_to_action" label="Supplier" required>
                  <app-select
                    @search="onSupplierSearch"
                    @input="onChangeSupplier"
                    v-model="form.supplier"
                    :options="supplier_options"
                    :clearable="false"
                    :readonly="invoice_to_action"
                    :display-field="option => `${option.trade_name === option.legal_name ? option.trade_name : `${option.trade_name} (${option.legal_name})`}`"
                    label="trade_name"
                    placeholder="Supplier"
                    :error="$v.form.supplier.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.supplier.$dirty && !$v.form.supplier.required">Required field</div>
                  </template>
                </app-form-group>
                <template v-if="form.supplier?.verified">
                  <app-form-group label="Transaction item" required>
                    <app-select
                      v-model="form.transaction_item"
                      :options="transaction_items_options"
                      :clearable="false"
                      label="name_expense"
                      placeholder="Transaction item"
                      :error="$v.form.transaction_item.$error"
                    />
                    <template #error>
                      <div v-if="$v.form.transaction_item.$dirty && !$v.form.transaction_item.required">Required field</div>
                    </template>
                  </app-form-group>
                  <app-form-group label="Total amount" label-for="amount_total" required>
                    <app-fin-number
                      v-model="form.amount_total"
                      id="amount_total"
                      name="amount_total"
                      :error="$v.form.amount_total.$error"
                    />
                    <template #error>
                      <div v-if="$v.form.amount_total.$dirty && !$v.form.amount_total.required">Required field</div>
                    </template>
                  </app-form-group>
                  <app-form-group label="Payment method" required>
                    <app-select
                      v-model="form.supplier_payment_method"
                      :options="form.supplier?.payment_methods"
                      :clearable="false"
                      :searchable="false"
                      label="public_name"
                      placeholder="Payment method"
                      :error="$v.form.supplier_payment_method.$error"
                    />
                    <template #error>
                      <div v-if="$v.form.supplier_payment_method.$dirty && !$v.form.supplier_payment_method.required">Required field</div>
                    </template>
                  </app-form-group>
                  <app-form-group
                    v-if="form.supplier_payment_method?.payment_method?.code === 'gepg'"
                    label="GePG number"
                    required label-for="gepg_number"
                  >
                    <app-input
                      v-model="form.gepg_number"
                      placeholder="GePG number"
                      id="gepg_number"
                      :error="$v.form.gepg_number.$error"
                    />
                    <template #error>
                      <div v-if="$v.form.gepg_number.$dirty && !$v.form.gepg_number.required">Required field</div>
                    </template>
                  </app-form-group>
                  <app-form-group :label="is_date ? 'Due date' : 'Payment plans'" required>
                    <template v-if="!invoice_to_action" #additional>
                      <app-switcher v-model="is_date" class="switcher--additional" />
                    </template>
                    <app-date-picker
                      v-if="is_date"
                      v-model="form.due_date"
                      placeholder="Specify a date"
                      :error="$v.form.due_date.$error"
                    />
                    <div v-else class="arrange-popup__part">
                      <div class="transfer-sortable transfer-sortable--full">
                        <div v-for="(element, index) in form.payment_plans" :key="`own_item_${index}`" class="transfer-sortable__item transfer-sortable__item--mb-20">
                          <app-flex-grid :rows="[[1, 1]]" margin-bottom="0">
                            <template #col-1-1>
                              <app-form-group label="Date" class="form-group--no-indent" required>
                                <app-date-picker
                                  v-model="element.date"
                                  :clearable="false"
                                  placeholder="Specify a date"
                                  :error="$v.form.payment_plans.$each[index].date.$error"
                                />
                                <template #error>
                                  <div v-if="$v.form.payment_plans.$each[index].date.$dirty && !$v.form.payment_plans.$each[index].date.required">Required field</div>
                                </template>
                              </app-form-group>
                            </template>
                            <template #col-1-2>
                              <app-form-group label="Amount" :label-for="`amount-${index}`" required class="form-group--no-indent">
                                <app-fin-number
                                  v-model="element.amount"
                                  :id="`amount-${index}`"
                                  placeholder="0"
                                  name="amount"
                                  :error="$v.form.payment_plans.$each[index].amount.$error"
                                />
                                <template #error>
                                  <div v-if="$v.form.payment_plans.$each[index].amount.$dirty && !$v.form.payment_plans.$each[index].amount.required">Required field</div>
                                </template>
                              </app-form-group>
                            </template>
                          </app-flex-grid>
                          <button
                            v-if="index"
                            @click="onRemoveItem(index)"
                            class="transfer-sortable__close"
                            type="button"
                          >
                            <img src="@/assets/img/delete-transfer.svg" alt="">
                          </button>
                          <button v-if="!index" @click="onAddItem" type="button" class="transfer-sortable__add"></button>
                        </div>
                      </div>
                    </div>
                    <template #error>
                      <div v-if="$v.form.due_date.$dirty && !$v.form.due_date.required">Required field</div>
                    </template>
                  </app-form-group>
                </template>
                <div v-if="form.supplier?.verified === false" class="popup__warning">
                  Payment methods have not been verified for this supplier. Please reach out to the accounting
                  department to complete the information. After that, you can add the invoice.
                </div>
              </template>
            </app-grid>
            <app-flex-grid v-if="efd_details" :rows="[['220px', '220px', 1, '145px'], ['220px', '220px', '220px', '220px']]">
              <template #col-1-1>
                <app-form-field label="Verification code" label-for="code" required>
                  <app-input
                    v-model="form.efd_receipt.verification_code"
                    :error="$v.form.efd_receipt.verification_code.$error"
                    id="code"
                    placeholder="Verification code"
                    name="code"
                  />
                  <template #error>
                    <div v-if="$v.form.efd_receipt.verification_code.$dirty && !$v.form.efd_receipt.verification_code.required">Required field</div>
                  </template>
                </app-form-field>
              </template>
              <template #col-1-2>
                <app-form-field label="Date" required>
                  <app-date-picker
                    v-model="form.efd_receipt.date"
                    :clearable="false"
                    :disabled-date="disableFutureDates"
                    placeholder="Date"
                    :error="$v.form.efd_receipt.date.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.efd_receipt.date.$dirty && !$v.form.efd_receipt.date.required">Required field</div>
                  </template>
                </app-form-field>
              </template>
              <template #col-1-3>
                <app-form-field label="Time" required>
                  <app-input
                    v-model="form.efd_receipt.time"
                    v-mask="'99:99:99'"
                    placeholder="00:00:00"
                    autocomplete="off"
                    id="time"
                    name="time"
                    @paste.native.prevent
                    :error="$v.form.efd_receipt.time.$error"
                  />
                  <template #error>
                    <div v-if="$v.form.efd_receipt.time.$dirty && !$v.form.efd_receipt.time.timeRegex">Wrong time</div>
                    <div v-if="$v.form.efd_receipt.time.$dirty && !$v.form.efd_receipt.time.required">Required field</div>
                  </template>
                </app-form-field>
              </template>
              <template #col-1-4>
                <app-form-field label="Receipt copy" required>
                  <app-uploader
                    v-model="form.efd_receipt.receipt_copy"
                    :extensions="['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/zip', 'application/pdf', 'image/jpeg', 'image/png', 'image/tiff', 'image/bmp']"
                    formDataFormats="DOC,DOCX,XLS,XLSX,ZIP,PDF,JPEG,BMP,PNG,TIFF"
                    class="custom-uploader--flex"
                    :readonly="receipt_same"
                  />
                  <template #error>
                    <div v-if="$v.form.efd_receipt.receipt_copy.$dirty && !$v.form.efd_receipt.receipt_copy.required">Required field</div>
                  </template>
                </app-form-field>
              </template>
              <template #col-2-1>
                <app-form-field label="Total amount" label-for="amount_total_efd" required>
                  <app-fin-number
                    @change.native="onChangeTotal"
                    v-model="form.efd_receipt.amount_total"
                    :error="$v.form.efd_receipt.amount_total.$error"
                    id="amount_total_efd"
                    name="amount_total_efd"
                    key="amount_total_efd"
                  />
                  <template #error>
                    <div v-if="$v.form.efd_receipt.amount_total.$dirty && !$v.form.efd_receipt.amount_total.required">Required field</div>
                  </template>
                </app-form-field>
              </template>
              <template #col-2-2>
                <app-form-field v-if="form.supplier?.vat === false" label="Non-creditable" label-for="non_creditable_amount">
                  <app-fin-number
                    v-model="form.efd_receipt.non_creditable_amount"
                    id="non_creditable_amount"
                    name="non_creditable_amount"
                    key="non_creditable_amount"
                    readonly
                  />
                </app-form-field>
                <app-form-field v-if="form.supplier?.vat === true" label="Exempted" label-for="amount_exempted" required>
                  <app-fin-number
                    @change.native="onChangeExempted"
                    v-model="form.efd_receipt.amount_exempted"
                    :error="$v.form.efd_receipt.amount_exempted.$error"
                    id="amount_exempted"
                    name="amount_exempted"
                  />
                  <template #error>
                    <div v-if="$v.form.efd_receipt.amount_exempted.$dirty && !$v.form.efd_receipt.amount_exempted.required">Required field</div>
                  </template>
                </app-form-field>
              </template>
              <template #col-2-3>
                <app-form-group v-if="form.supplier?.vat === true" label="VAT" label-for="amount_vat" required class="form-group--no-indent">
                  <template #additional>
                    <app-checkbox
                      v-model="manual_adjustment"
                      label="Manual"
                    />
                  </template>
                  <app-fin-number
                    v-model="form.efd_receipt.amount_vat"
                    :error="$v.form.efd_receipt.amount_vat.$error"
                    id="amount_vat"
                    name="amount_vat"
                    :readonly="!manual_adjustment"
                  />
                  <template #error>
                    <div v-if="$v.form.efd_receipt.amount_vat.$dirty && !$v.form.efd_receipt.amount_vat.required">Required field</div>
                  </template>
                </app-form-group>
              </template>
              <template #col-2-4>
                <app-form-field v-if="form.supplier?.vat === true" label="Excluded VAT" label-for="amount_vat_exc" required>
                  <app-fin-number
                    v-model="form.efd_receipt.amount_vat_exc"
                    :error="$v.form.efd_receipt.amount_vat_exc.$error"
                    id="amount_vat_exc"
                    name="amount_vat_exc"
                    :readonly="!manual_adjustment"
                  />
                  <template #error>
                    <div v-if="$v.form.efd_receipt.amount_vat_exc.$dirty && !$v.form.efd_receipt.amount_vat_exc.required">Required field</div>
                  </template>
                </app-form-field>
              </template>
            </app-flex-grid>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submit_transaction">
                {{ invoice_to_action ? 'Save' : 'Add' }}
              </app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns'
import { helpers, required, requiredIf } from 'vuelidate/lib/validators'
import debounce from 'lodash/debounce'
import {
  getAdministrativeLegalEntities,
  getAdministrativeSuppliers,
  getCurrencyList,
  getEmployeesListSearch,
  getFinancePaymentMethods,
  getFinancePosTransactionsSource,
  getFinanceSupplierInvoiceList,
  getFinanceTransactionsItemsList,
  patchFinanceSupplierInvoice,
  postFinanceSupplierInvoice,
} from '@/http'

const timeRegex = helpers.regex('timeRegex', /^([0-1][0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])?$/i)

export default {
  name: 'FinanceSupplierInvoices',
  data() {
    return {
      manual_adjustment: false,
      page_loaded: false,
      filter_paid: 'unpaid',
      filter_paid_options: [
        { value: 'unpaid', label: 'Unpaid' },
        { value: 'paid', label: 'Paid' },
        { value: '', label: 'All' },
      ],
      filter_legal_entity: null,
      filter_payment_method: null,
      filter_source: null,
      filter_supplier: null,
      table: {
        items: [],
        columns: [
          'date',
          'supplier',
          'amount',
          'invoice_number',
          'responsible',
          'payment_method',
          'account',
          'payment_plans',
          'attachment',
          'controls'
        ],
        options: {
          headings: {
            'date': 'Date',
            'supplier': 'Supplier',
            'amount': 'Amount',
            'invoice_number': 'Invoice number',
            'responsible': 'Responsible',
            'payment_method': 'Payment method',
            'account': 'Account',
            'payment_plans': 'Payment',
            'attachment': 'Attachment',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader: false,
      invoice_to_action: null,
      form: {},
      legal_entity_options: [],
      payment_method_options: [],
      pos_transaction_source_options: [],
      supplier_options: [],
      currency_options: [
        { code: 'TZS', label: 'TZS' },
        { code: 'USD', label: 'USD' },
      ],
      responsible_options: [],
      transaction_items_options: [],
      is_date: true,
      efd_details: false,
      repaint_key: 1,
      receipt_same: false,
      show_accounting_date: false,
    }
  },
  validations: {
    form: {
      date: { required },
      accounting: {
        required: requiredIf(function() {
          return this.show_accounting_date
        })
      },
      legal_entity: { required },
      invoice_number: { required },
      currency: { required },
      responsible: { required },
      supplier: { required },
      amount_total: { required },
      supplier_payment_method: { required },
      transaction_item: { required },
      gepg_number: {
        required: requiredIf(function() {
          return this.form.supplier_payment_method?.payment_method?.code === 'gepg'
        })
      },
      due_date: {
        required: requiredIf(function() {
          return this.is_date && !this.invoice_to_action
        })
      },
      payment_plans: {
        $each: {
          amount: {
            required: requiredIf(function() {
              return !this.is_date
            })
          },
          date: {
            required: requiredIf(function() {
              return !this.is_date
            })
          },
        }
      },
      efd_receipt: {
        date: {
          required: requiredIf(function() {
            return this.efd_details && !this.invoice_to_action
          }),
        },
        time: {
          required: requiredIf(function() {
            return this.efd_details && !this.invoice_to_action
          }),
          timeRegex
        },
        verification_code: {
          required: requiredIf(function() {
            return this.efd_details && !this.invoice_to_action
          }),
        },
        receipt_copy: {
          required: requiredIf(function() {
            return this.efd_details && !this.invoice_to_action
          }),
        },
        amount_vat_exc: {
          required: requiredIf(function() {
            return this.efd_details && this.form.supplier?.vat && !this.invoice_to_action
          })
        },
        amount_vat: {
          required: requiredIf(function() {
            return this.efd_details && this.form.supplier?.vat && !this.invoice_to_action
          })
        },
        amount_exempted: {
          required: requiredIf(function() {
            return this.efd_details && this.form.supplier?.vat && !this.invoice_to_action
          })
        },
        amount_total: {
          required: requiredIf(function() {
            return this.efd_details && !this.invoice_to_action
          })
        }
      }
    }
  },
  created() {
    Promise.all([
      getFinancePaymentMethods(),
      getFinancePosTransactionsSource(),
      getFinanceTransactionsItemsList({ expense: true }),
      getAdministrativeLegalEntities(),
      this.fetchItems()
    ])
      .then(res => {
        this.payment_method_options = res[0].data
        this.pos_transaction_source_options = res[1].data
        this.transaction_items_options = res[2].data
        this.legal_entity_options = res[3].data
        this.page_loaded = true
      })
  },
  methods: {
    async fetchItems() {
      this.table_preloader = true
      const response = await getFinanceSupplierInvoiceList({
        page: this.pagination.page,
        page_size: 20,
        payment_status: this.filter_paid,
        supplier: this.filter_supplier?.id,
        payment_method: this.filter_payment_method,
        legal_entity: this.filter_legal_entity,
        source: this.filter_source,
        reservations: false,
        status: 'confirmed',
        // ordering: this.filter_paid === 'unpaid' ? 'date' : ''
      })
      this.table.items = response.data.results
      this.pagination.pages = response.data.pages
      this.pagination.count = response.data.count
      this.table_preloader = false
    },
    onChangeFilter() {
      this.pagination.page = 1
      this.fetchItems()
    },
    onChangeReceiptSame() {
      if (this.receipt_same) this.form.efd_receipt.receipt_copy = {...this.form.attachment}
      else this.form.efd_receipt.receipt_copy = null
      this.$v.form.efd_receipt.receipt_copy.$touch()
      this.repaint_key += 1
    },
    onChangeEFDDetails() {
      if (!this.efd_details) {
        this.receipt_same = false
        this.form.efd_receipt.receipt_copy = null
      }
      this.repaint_key += 1
    },
    onChangeAttachment() {
      if (!this.form.attachment.id) {
        this.form.efd_receipt.receipt_copy = null
        this.receipt_same = false
        this.repaint_key += 1
      }
    },
    calculateVAT() {
      const rate = 18 / 118
      // const vat = (((parseFloat(this.form.efd_receipt.amount_total) || 0) - (parseFloat(this.form.efd_receipt.amount_exempted) || 0)) * rate).toFixed(2)
      // let [integerPart, decimalPart] = vat.split('.')
      // integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
      // if (decimalPart) decimalPart = decimalPart.slice(0, 2)
      this.form.efd_receipt.amount_vat = (((parseFloat(this.form.efd_receipt.amount_total) || 0) - (parseFloat(this.form.efd_receipt.amount_exempted) || 0)) * rate).toFixed(2)
      this.form.efd_receipt.amount_vat_exc = ((parseFloat(this.form.efd_receipt.amount_total) || 0) - (parseFloat(this.form.efd_receipt.amount_exempted) || 0) - parseFloat(this.form.efd_receipt.amount_vat)).toFixed(2)
    },
    onChangeTotal() {
      if (this.form.supplier?.vat === false) {
        this.form.efd_receipt.non_creditable_amount = this.form.efd_receipt.amount_total
      } else {
        this.calculateVAT()
      }
      this.repaint_key += 1
    },
    onChangeExempted() {
      this.calculateVAT()
      this.repaint_key += 1
    },
    onCheckInvoiceForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit_transaction.preload = true
      const formData = { ...this.form, efd_receipt: {...this.form.efd_receipt} }
      if (formData.attachment) formData.attachment = formData.attachment.id
      formData.supplier = formData.supplier.id
      formData.responsible = formData.responsible.id
      formData.transaction_item = formData.transaction_item.id
      formData.supplier_payment_method = formData.supplier_payment_method.id

      if (this.invoice_to_action) {
        delete formData.efd_receipt
        patchFinanceSupplierInvoice(this.invoice_to_action, formData)
          .then(() => {
            this.fetchItems()
            this.$notify({
              type: 'success',
              title: 'Changes saved',
            })
            this.$modal.hide('invoice-modal')
          })
          .catch(() => { this.$refs.submit_transaction.preload = false })
      } else {
        if (!this.efd_details) {
          formData.efd_receipt = null
        } else {
          formData.efd_receipt.receipt_copy = formData.efd_receipt.receipt_copy.id
          if (this.form.supplier.vat) {
            delete formData.efd_receipt.non_creditable_amount
          } else {
            delete formData.efd_receipt.amount_exempted
            delete formData.efd_receipt.amount_vat
            delete formData.efd_receipt.amount_vat_exc
          }
        }
        if (this.is_date) delete formData.payment_plans
        else delete formData.due_date
        postFinanceSupplierInvoice(formData)
          .then(() => {
            this.fetchItems()
            this.$notify({
              type: 'success',
              title: 'Invoice created',
            })
            this.$modal.hide('invoice-modal')
            this.efd_details = false
            this.receipt_same = false
          })
          .catch(() => { this.$refs.submit_transaction.preload = false })
      }
    },
    onShowInvoiceModal(row) {
      this.invoice_to_action = row?.id
      if (row) {
        this.is_date = false
        this.form = {
          supplier: row.supplier,
          legal_entity: row.legal_entity.code,
          date: row.date,
          accounting: row.accounting,
          amount_total: row.amount_total,
          attachment: row.attachment,
          currency: row.currency.code,
          responsible: row.responsible,
          payment_plans: row.payment_plans,
          invoice_number: row.invoice_number,
          supplier_payment_method: row.supplier_payment_method,
          gepg_number: row.gepg_number,
          transaction_item: row.transaction_item,
        }
      } else {
        this.efd_details = false
        this.is_date = true
        this.show_accounting_date = false
        this.form = {
          date: format(new Date(), 'yyyy-MM-dd'),
          payment_plans: [{ date: null, amount: null}],
          supplier_payment_method: null,
          legal_entity: this.legal_entity_options[0].code,
          currency: 'TZS',
          archive: true,
          efd_receipt: {
            amount_vat_exc: '',
            amount_vat: '',
            amount_exempted: '',
            non_creditable_amount: '',
            amount_total: '',
            receipt_copy: null,
          },
        }
      }
      this.$v.form.$reset()
      this.$modal.show('invoice-modal')
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onChangeSupplier(e) {
      this.form.efd_receipt.amount_total = ''
      this.form.efd_receipt.amount_vat_exc = ''
      this.form.efd_receipt.amount_vat = ''
      this.form.efd_receipt.amount_exempted = ''
      this.form.efd_receipt.non_creditable_amount = ''
      this.form.transaction_item = this.form.supplier.transaction_item
      this.form.supplier_payment_method = e.payment_methods[0]
      this.repaint_key += 1
    },
    onAddItem() {
      this.form.payment_plans.push({})
    },
    onRemoveItem(index) {
      this.form.payment_plans.splice(index, 1)
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    amountFormat(amount) {
      return new Intl.NumberFormat('ru-RU').format(amount)
    },
    disableFutureDates(date) {
      const d = this.normalizeDate(date)
      const today = this.normalizeDate(new Date())
      return d > today
    },
    normalizeDate(val) {
      const date = new Date(val)
      date.setHours(0, 0, 0, 0)
      return date
    },
    onSupplierSearch(search, loading) {
      loading(true)
      this.searchSupplier(loading, search, this)
    },
    searchSupplier: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.supplier_options = []
      } else {
        getAdministrativeSuppliers({search}).then(response => {
          vm.supplier_options = response.data
          loading(false)
        })
      }
    }, 350),
    onEmployeeSearch(search, loading) {
      loading(true)
      this.searchEmployee(loading, search, this)
    },
    searchEmployee: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.responsible_options = []
      } else {
        getEmployeesListSearch({search}).then(response => {
          vm.responsible_options = response.data
          loading(false)
        })
      }
    }, 350),
  }
}
</script>
